import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, X, CheckCircle2, Share2, Calendar, MapPin, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import EventShareDialog from "./EventShareDialog";

const ROUTE_FOR_KIND = { match: "/matches", tournament: "/tournaments", league: "/leagues", lesson: "/lessons" };

function dismissedKey(kind, id) { return `pp-banner-dismissed:${kind}:${id}`; }
function isDismissed(kind, id, start) {
  const v = localStorage.getItem(dismissedKey(kind, id));
  if (!v) return false;
  // If the stored dismiss is older than the current event start, treat as not dismissed.
  // This way rescheduled events resurface the banner.
  if (!start) return true;
  try {
    const dismissedAt = Number(v) || 0;
    const eventStart = new Date(start).getTime();
    return dismissedAt > eventStart - 25 * 60 * 60 * 1000; // dismissed during this event's 24h window
  } catch { return true; }
}
function markDismissed(kind, id) { localStorage.setItem(dismissedKey(kind, id), String(Date.now())); }

function timeUntil(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "now";
  const h = Math.floor(ms / (60 * 60 * 1000));
  const m = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (h <= 0) return `in ${m}m`;
  if (h < 24) return `in ${h}h ${m}m`;
  return `in ${Math.round(h / 24)}d`;
}

/**
 * Floating in-app reminder banner.
 * Pulls /api/me/upcoming, surfaces the soonest event starting in <24h that
 * the user has not yet dismissed. CTAs: Confirm "I'll be there" + Share + View.
 */
export default function UpcomingBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); return; }
    try {
      const { data } = await api.get("/me/upcoming?window_hours=24");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      refresh();
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [refresh, user]);

  const next = items.find((it) => !isDismissed(it.kind, it.id, it.start));
  if (!user || !next) return null;

  const route = ROUTE_FOR_KIND[next.kind] || `/${next.kind}s`;
  const start = next.start ? new Date(next.start) : null;
  const whenLabel = next.all_day
    ? (start ? start.toLocaleDateString() + " · All day" : "")
    : (start ? start.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "");

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${next.kind}/${next.id}/confirm`);
      toast.success("Confirmed — see you there!");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    markDismissed(next.kind, next.id);
    setItems((arr) => [...arr]); // force rerender to recompute `next`
  };

  const baseShareLink = next.join_token
    ? `${window.location.origin}/join/${next.kind}/${next.join_token}`
    : `${window.location.origin}${route}/${next.id}`;
  const shareLink = user?.id ? `${baseShareLink}${baseShareLink.includes("?") ? "&" : "?"}ref=${user.id}` : baseShareLink;

  const nativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: next.title, text: `Join "${next.title}" on Pick2Play`, url: shareLink });
      } else {
        await navigator.clipboard.writeText(shareLink);
        toast.success("Link copied — share it with a friend");
      }
    } catch (err) {
      // navigator.share rejects with AbortError when the user dismisses the
      // share sheet. Anything else is worth knowing about during dev.
      if (err && err.name !== "AbortError") {
        // eslint-disable-next-line no-console
        console.warn("[upcoming] share failed:", err);
      }
    }
  };

  return (
    <div
      data-testid="upcoming-banner"
      className={`fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-50 max-w-md sm:w-[420px] mx-auto sm:mx-0 ${next.at_risk && !next.is_owner ? "ring-1 ring-[#FFD60A]/40" : ""}`}
      style={{ bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
    >
      <div className={`pp-card-elevated p-4 ${next.at_risk && !next.is_owner ? "border-[#FFD60A]/40 shadow-[0_12px_40px_rgba(255,214,10,0.15)]" : "border-[#00E676]/40 shadow-[0_12px_40px_rgba(0,230,118,0.15)]"} backdrop-blur`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`w-9 h-9 rounded-full ${next.at_risk && !next.is_owner ? "bg-[#FFD60A]/15 border-[#FFD60A]/30 text-[#FFD60A]" : "bg-[#00E676]/15 border-[#00E676]/30 text-[#00E676]"} border flex items-center justify-center`}>
              {next.at_risk && !next.is_owner ? <AlertTriangle size={16} /> : <Clock size={16} />}
            </span>
            <div>
              <div className={`text-[10px] uppercase tracking-wider ${next.at_risk && !next.is_owner ? "text-[#FFD60A]" : "text-[#00E676]"}`} data-testid="upcoming-when">
                {next.at_risk && !next.is_owner ? "Needs more players" : `Starting ${timeUntil(next.start)}`}
              </div>
              <Link to={`${route}/${next.id}`} className="font-bold text-sm hover:text-[#00E676]" data-testid="upcoming-title">
                {next.title}
              </Link>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" data-testid="upcoming-dismiss"
            className="p-1 text-zinc-500 hover:text-white"><X size={14} /></button>
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-xs text-zinc-400">
          {whenLabel && <span className="flex items-center gap-1"><Calendar size={11} /> {whenLabel}</span>}
          {next.location && <span className="flex items-center gap-1"><MapPin size={11} /> {next.location}</span>}
          {next.sport && <span className="pp-chip text-[10px]">{next.sport}</span>}
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {next.confirmed ? (
            <span className="pp-chip pp-chip-active flex items-center gap-1 text-xs" data-testid="upcoming-confirmed-badge">
              <CheckCircle2 size={12} /> Confirmed
            </span>
          ) : (
            <button onClick={confirm} disabled={busy} data-testid="upcoming-confirm"
              className="pp-btn-primary px-3 py-2 rounded-lg text-xs flex items-center gap-1">
              <CheckCircle2 size={12} /> {busy ? "…" : "I'll be there"}
            </button>
          )}
          {next.is_owner ? (
            <EventShareDialog
              eventKind={next.kind}
              eventId={next.id}
              eventTitle={next.title}
              trigger={
                <button data-testid="upcoming-invite" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1">
                  <Share2 size={12} /> Invite a friend
                </button>
              }
            />
          ) : next.at_risk ? (
            <button onClick={nativeShare} data-testid="upcoming-bring-friend"
              className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1 border-[#FFD60A]/40 text-[#FFD60A] hover:bg-[#FFD60A]/10">
              <Share2 size={12} /> Bring a friend
            </button>
          ) : null}
          <Link to={`${route}/${next.id}`} data-testid="upcoming-view"
            className="ms-auto text-xs text-zinc-400 hover:text-[#00E676] flex items-center gap-1">
            View <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
